import { arrayOf, bool, func, object, oneOfType, string } from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { withTheme } from 'styled-components';

import { DURATION } from 'ravenjs/constants/transitions';
import Menu from 'ravenjs/lib/Menu';
import PopoverContent from 'ravenjs/lib/PopoverContent';
import Tooltip from 'ravenjs/lib/Tooltip';
import Typography from 'ravenjs/lib/Typography';
import { callFunc } from 'ravenjs/utils/actions';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import Row from 'ravenjs/lib/Row';
import { getThemeProps } from 'ravenjs/utils/theme';

import { DEFAULT_COLORS } from 'constants/colors';
import MESSAGES from 'constants/messages';
import { APP_PERMISSIONS } from 'constants/permissions';
import Acl from 'modules/acl';
import { isNavigationV2Enabled as isNavV2Enabled } from 'utils/company';

import { AlertIcon, AlertIconCounter, ThrashIcon } from 'components/Icons';
import {
    DESKTOP_BREAKPOINT,
    DROPDOWN_OPEN_EVENT,
    HOVER_INTENT_DELAY,
    KEYBOARD_KEYS,
    SUBMENU_CLOSE_DELAY,
} from 'constants/navigation';

import NotificationItem from './NotificationItem';
import NotificationsCounter from './NotificationsCounter';

const PopoverStylesV1 = styled(PopoverContent)`
    bottom: 0;
    margin-left: 2px;
    &:before {
        background: transparent;
        box-shadow: none;
        border-bottom: 8px solid transparent;
        border-left: 8px solid transparent;
        border-right: 8px solid ${DEFAULT_COLORS.WHITE};
        border-top: 8px solid ${DEFAULT_COLORS.WHITE};
        top: -7px;
    }
`;

const PopoverStylesV2 = styled(PopoverContent)`
    bottom: 0;
    margin-left: 2px;
    margin-top: 30px;
    &:before {
        display: none;
    }
`;

const ContentStyledV1 = styled.div`
    border: 1px solid ${DEFAULT_COLORS.DARK};
    background-color: ${DEFAULT_COLORS.WHITE};
    min-width: 16.75rem;
    padding: 0;
`;

const ContentStyledV2 = styled.div`
    border: 1px solid ${DEFAULT_COLORS.GREY_22};
    background-color: ${DEFAULT_COLORS.WHITE};
    width: 400px;
    padding: 0;
    border-radius: 4px;
    box-shadow: 0 2px 8px -2px rgba(40, 40, 40, 0.16);
`;

const NotificationItems = styled.div`
    max-height: 475.5px;
    overflow-y: scroll;

    @media (max-height: 768px) {
        max-height: 350px;
    }
`;

const DeleteAllStyledV1 = styled.div`
    align-items: center;
    background: ${DEFAULT_COLORS.BANNER_RED};
    display: flex;
    justify-content: center;
    min-height: 2.25rem;
    cursor: pointer;
`;

const DeleteAllStyledV2 = styled.div`
    align-items: center;
    background: ${DEFAULT_COLORS.BANNER_RED};
    display: flex;
    justify-content: center;
    min-height: 2.25rem;
    cursor: pointer;
    /* V2 keyboard navigation focus styles */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: -2px;
    }
`;

const IconWrapper = styled.div`
    align-items: center;
    display: flex;
    position: relative;
`;

const IconWrapperV2 = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    /* Adjust the notification icon for non-square viewBox causing visual left-shift */
    & > svg {
        margin-right: 2px;
    }
`;

const MenuWrapperV1 = styled.div`
    height: 100%;
`;

const MenuWrapperV2 = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -12px;
    padding: 12px;
    box-sizing: content-box;
    &:focus {
        outline: none;
    }
`;

const NoNotificationsRow = styled(Row)`
    margin-top: 2.99rem;
    display: flex;
    justify-content: center;
    height: 5.438rem;
    align-items: center;
`;

const NotificationsStyledV1 = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    height: 100%;
    user-select: none;
    ${({ theme }) => theme.media.up('md')`
        margin-right: 0.53rem;
        margin-left: 0.53rem;
    `};
    ${({ theme }) => theme.media.down('md')`
        margin-right: 0;
        margin-left: 0;
    `};
`;

const NotificationsStyledV2 = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 100%;
    width: 100%;
    user-select: none;
`;

const NotificationsWrapperV1 = styled.div`
    height: 100%;
    position: relative;
`;

const NotificationsWrapperV2 = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    overflow: visible;
`;

const ViewMoreStyledV1 = styled(Row)`
    background: ${DEFAULT_COLORS.LIGHTBLUE};
    cursor: ${({ disableClick }) => (disableClick ? 'default' : 'pointer')};
    height: 2.25rem;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0;
    :hover {
        background: ${DEFAULT_COLORS.BLUE};
    }
`;

const ViewMoreStyledV2 = styled.div`
    display: flex;
    background: ${DEFAULT_COLORS.LIGHTBLUE};
    cursor: ${({ disableClick }) => (disableClick ? 'default' : 'pointer')};
    height: 2.25rem;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0;
    :hover {
        background: ${DEFAULT_COLORS.BLUE};
    }
    /* V2 keyboard navigation focus styles */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: -2px;
    }
`;

function NotificationsMenu({
    errorMessage,
    hasError,
    iconRef,
    menuRef,
    noNotificationsMessage,
    notifications,
    onClickNotification,
    onClickNotificationIcon,
    showNotificationMenu,
    hideNotificationMenu,
    onDeleteNotification,
    onViewMore,
    open: isMenuOpen,
    totalUnseen,
    total,
    onClickDeleteAll,
    history,
    theme,
    onMenuOpenChange,
    tooltipText,
    showTooltip,
    onTooltipHover,
    onTooltipLeave,
}) {
    const [isDesktop, setIsDesktop] = useState(
        typeof globalThis !== 'undefined' && globalThis.innerWidth >= DESKTOP_BREAKPOINT
    );
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);

    const hoverIntentTimeoutRef = useRef(null);
    const closeDelayTimeoutRef = useRef(null);
    const focusTimeoutRef = useRef(null);
    const menuWrapperRef = useRef(null);
    // Separate ref for ContentStyled to properly query notification items (menuRef is used by Menu component)
    const contentRef = useRef(null);

    const isNavigationV2Enabled = isNavV2Enabled();

    const clearAllTimeouts = useCallback(() => {
        if (hoverIntentTimeoutRef.current) {
            clearTimeout(hoverIntentTimeoutRef.current);
            hoverIntentTimeoutRef.current = null;
        }
        if (closeDelayTimeoutRef.current) {
            clearTimeout(closeDelayTimeoutRef.current);
            closeDelayTimeoutRef.current = null;
        }
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
            focusTimeoutRef.current = null;
        }
    }, []);

    const notifyDropdownOpen = useCallback(() => {
        globalThis.dispatchEvent(
            new CustomEvent(DROPDOWN_OPEN_EVENT, {
                detail: { source: 'notifications' },
            })
        );
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const newIsDesktop = globalThis.innerWidth >= DESKTOP_BREAKPOINT;
            if (newIsDesktop !== isDesktop) {
                setIsDesktop(newIsDesktop);
                if (!newIsDesktop && isNavigationV2Enabled) {
                    clearAllTimeouts();
                    hideNotificationMenu();
                }
            }
        };

        globalThis.addEventListener('resize', handleResize);
        return () => globalThis.removeEventListener('resize', handleResize);
    }, [isDesktop, isNavigationV2Enabled, hideNotificationMenu, clearAllTimeouts]);

    // Click-outside detection (V2 only)
    useEffect(() => {
        const handleClickOutside = event => {
            if (!isNavigationV2Enabled) {
                return;
            }

            const isInIconContainer =
                iconRef?.current &&
                typeof iconRef.current.contains === 'function' &&
                iconRef.current.contains(event.target);

            // Check if the click is within the menu content (ContentStyled)
            const isInMenuContent =
                contentRef?.current &&
                typeof contentRef.current.contains === 'function' &&
                contentRef.current.contains(event.target);

            // Only close if the click is outside both the container and menu content
            if (!isInIconContainer && !isInMenuContent) {
                clearAllTimeouts();
                hideNotificationMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isNavigationV2Enabled, hideNotificationMenu, clearAllTimeouts, iconRef]);

    // Listen for other dropdowns opening - close this one immediately
    useEffect(() => {
        const handleOtherDropdownOpen = event => {
            if (!isNavigationV2Enabled) {
                return;
            }
            // If another dropdown opened (not this one), close immediately
            if (event.detail && event.detail.source !== 'notifications') {
                clearAllTimeouts();
                hideNotificationMenu();
            }
        };

        globalThis.addEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
        return () => globalThis.removeEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);
    }, [isNavigationV2Enabled, hideNotificationMenu, clearAllTimeouts]);

    useEffect(() => {
        if (onMenuOpenChange) {
            onMenuOpenChange(isMenuOpen);
        }
    }, [isMenuOpen, onMenuOpenChange]);

    // Cleanup timeouts on unmounting
    useEffect(() => {
        return () => clearAllTimeouts();
    }, [clearAllTimeouts]);

    // V2 hover handler with hover-intent algorithm (for mouse enter only)
    const handleShowMenu = useCallback(() => {
        if (isNavigationV2Enabled) {
            // Clear any pending close timeout when entering
            if (closeDelayTimeoutRef.current) {
                clearTimeout(closeDelayTimeoutRef.current);
                closeDelayTimeoutRef.current = null;
            }

            // For desktop V2, use hover-intent delay
            if (isDesktop) {
                if (hoverIntentTimeoutRef.current) {
                    clearTimeout(hoverIntentTimeoutRef.current);
                }

                hoverIntentTimeoutRef.current = setTimeout(() => {
                    // Notify other dropdowns to close when this one opens
                    notifyDropdownOpen();
                    showNotificationMenu();
                }, HOVER_INTENT_DELAY);
            } else {
                notifyDropdownOpen();
                showNotificationMenu();
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            showNotificationMenu();
        }
    }, [isNavigationV2Enabled, isDesktop, showNotificationMenu, notifyDropdownOpen]);

    // V2 hide handler with delay tolerance (for mouse leave only, NOT for clicks)
    const handleHideMenu = useCallback(() => {
        if (isNavigationV2Enabled) {
            // Clear hover intent if the user moves away before intent is confirmed
            if (hoverIntentTimeoutRef.current) {
                clearTimeout(hoverIntentTimeoutRef.current);
                hoverIntentTimeoutRef.current = null;
            }

            // For desktop V2, use delay tolerance before closing
            if (isDesktop) {
                if (closeDelayTimeoutRef.current) {
                    clearTimeout(closeDelayTimeoutRef.current);
                }

                closeDelayTimeoutRef.current = setTimeout(() => {
                    hideNotificationMenu();
                }, SUBMENU_CLOSE_DELAY);
            } else {
                hideNotificationMenu();
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            hideNotificationMenu();
        }
    }, [isNavigationV2Enabled, isDesktop, hideNotificationMenu]);

    // Cancel close when re-entering the menu area (V2 only)
    const cancelClose = useCallback(() => {
        if (closeDelayTimeoutRef.current) {
            clearTimeout(closeDelayTimeoutRef.current);
            closeDelayTimeoutRef.current = null;
        }
    }, []);

    // Get focusable notification items within the dropdown
    const getNotificationItems = useCallback(() => {
        // Use contentRef instead of menuRef to properly query all notification items
        // (menuRef is used by Menu component and may not point to ContentStyled)
        if (contentRef?.current) {
            const items = contentRef.current.querySelectorAll('[data-notification-item="true"]');
            return Array.from(items);
        }
        return [];
    }, []);

    // Focus a specific notification item by index
    const focusNotificationItem = useCallback(
        index => {
            const items = getNotificationItems();
            if (items && items[index]) {
                items[index].focus();
            }
        },
        [getNotificationItems]
    );

    // Handle Escape key - close dropdown and return focus to bell icon
    const handleEscapeKey = useCallback(() => {
        clearAllTimeouts();
        hideNotificationMenu();
        setFocusedItemIndex(-1);
        // Return focus to the menu wrapper (bell icon container)
        if (menuWrapperRef.current) {
            menuWrapperRef.current.focus();
        }
    }, [clearAllTimeouts, hideNotificationMenu]);

    // Handle Enter or Space key - open dropdown or activate item
    const handleEnterOrSpaceKey = useCallback(() => {
        if (!isMenuOpen) {
            // Dropdown is closed - open it
            clearAllTimeouts();
            notifyDropdownOpen();
            showNotificationMenu();
            setFocusedItemIndex(0);
            // Store timeout ID in ref for cleanup during unmounting
            focusTimeoutRef.current = setTimeout(() => {
                focusNotificationItem(0);
            }, 100);
        } else if (focusedItemIndex >= 0) {
            // Dropdown is open and an item is focused - activate it
            const items = getNotificationItems();
            if (items && items[focusedItemIndex]) {
                items[focusedItemIndex].click();
            }
        }
    }, [
        isMenuOpen,
        focusedItemIndex,
        clearAllTimeouts,
        notifyDropdownOpen,
        showNotificationMenu,
        getNotificationItems,
        focusNotificationItem,
    ]);

    // Handle Arrow Down key - navigate down within the menu
    const handleArrowDownKey = useCallback(() => {
        const items = getNotificationItems();
        if (!items || items.length === 0) {
            return;
        }
        const nextIndex = focusedItemIndex < items.length - 1 ? focusedItemIndex + 1 : 0;
        setFocusedItemIndex(nextIndex);
        focusNotificationItem(nextIndex);
    }, [focusedItemIndex, getNotificationItems, focusNotificationItem]);

    // Handle Arrow Up key - navigate up within the menu
    const handleArrowUpKey = useCallback(() => {
        const items = getNotificationItems();
        if (!items || items.length === 0) {
            return;
        }
        const prevIndex = focusedItemIndex > 0 ? focusedItemIndex - 1 : items.length - 1;
        setFocusedItemIndex(prevIndex);
        focusNotificationItem(prevIndex);
    }, [focusedItemIndex, getNotificationItems, focusNotificationItem]);

    // Handle focus leaving the dropdown content area (V2 only)
    const handleContentBlur = useCallback(
        event => {
            if (!isNavigationV2Enabled || !isDesktop || !isMenuOpen) {
                return;
            }

            // Check if the new focus target (relatedTarget) is still within the dropdown content
            const contentElement = contentRef.current;
            const newFocusTarget = event.relatedTarget;

            if (!newFocusTarget || newFocusTarget === document.body) {
                return;
            }

            // If focus is moving to an element outside the content area, close the dropdown
            if (contentElement && !contentElement.contains(newFocusTarget)) {
                // Also check if focus is moving back to the menu wrapper (bell icon)
                if (menuWrapperRef.current && menuWrapperRef.current.contains(newFocusTarget)) {
                    // Focus is moving back to bell icon - don't close
                    return;
                }
                // Focus is leaving the dropdown entirely - close it
                clearAllTimeouts();
                hideNotificationMenu();
                setFocusedItemIndex(-1);
            }
        },
        [isNavigationV2Enabled, isDesktop, isMenuOpen, clearAllTimeouts, hideNotificationMenu]
    );

    // Handle keyboard navigation for the notification dropdown (V2 only)
    const handleKeyDown = useCallback(
        event => {
            if (!isNavigationV2Enabled || !isDesktop) {
                return;
            }

            switch (event.key) {
                case KEYBOARD_KEYS.ESCAPE:
                    if (isMenuOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        handleEscapeKey();
                    }
                    break;

                case KEYBOARD_KEYS.ENTER:
                case KEYBOARD_KEYS.SPACE:
                    event.preventDefault();
                    event.stopPropagation();
                    handleEnterOrSpaceKey();
                    break;

                case KEYBOARD_KEYS.ARROW_DOWN:
                    if (isMenuOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        handleArrowDownKey();
                    }
                    break;

                case KEYBOARD_KEYS.ARROW_UP:
                    if (isMenuOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        handleArrowUpKey();
                    }
                    break;

                case KEYBOARD_KEYS.TAB:
                    // V2: Allow Tab to navigate within the dropdown.
                    // The dropdown will close via handleContentBlur when focus leaves the content area
                    // Do NOT close the dropdown here - let natural Tab navigation work
                    break;

                default:
                    break;
            }
        },
        [
            isNavigationV2Enabled,
            isDesktop,
            isMenuOpen,
            handleEscapeKey,
            handleEnterOrSpaceKey,
            handleArrowDownKey,
            handleArrowUpKey,
        ]
    );

    const PopoverStyles = isNavigationV2Enabled ? PopoverStylesV2 : PopoverStylesV1;
    const ContentStyled = isNavigationV2Enabled ? ContentStyledV2 : ContentStyledV1;
    const ViewMoreStyled = isNavigationV2Enabled ? ViewMoreStyledV2 : ViewMoreStyledV1;
    const DeleteAllStyled = isNavigationV2Enabled ? DeleteAllStyledV2 : DeleteAllStyledV1;
    const MenuWrapper = isNavigationV2Enabled ? MenuWrapperV2 : MenuWrapperV1;

    function handleNotificationOnClick(id) {
        // Clear timeouts to prevent any pending hover actions
        clearAllTimeouts();
        callFunc(
            onClickNotification,
            notifications.find(notification => notification.notificationId === id)
        );
    }

    function handleNotificationOnDelete(id) {
        callFunc(
            onDeleteNotification,
            notifications.find(notification => notification.notificationId === id)
        );
    }

    function renderNotifications() {
        if (hasError) {
            return (
                <NoNotificationsRow>
                    <Typography color="error" type="noNewNotifications" gutterBottom="0">
                        {errorMessage}
                    </Typography>
                </NoNotificationsRow>
            );
        } else if (isEmpty(notifications)) {
            return (
                <NoNotificationsRow
                    display="flex"
                    justify="center"
                    height="5.438rem"
                    alignItems="center"
                >
                    <Typography type="noNewNotifications" gutterBottom="0">
                        {noNotificationsMessage}
                    </Typography>
                </NoNotificationsRow>
            );
        }

        return (
            <NotificationItems>
                {notifications.map(
                    ({ entityType, notificationId, summary, status, timestamp }, index) => {
                        return (
                            <NotificationItem
                                entityType={entityType}
                                key={notificationId}
                                id={notificationId}
                                isNavigationV2Enabled={isNavigationV2Enabled}
                                onClick={handleNotificationOnClick}
                                onViewMore={onViewMore}
                                onDelete={handleNotificationOnDelete}
                                onFocusNextItem={() => {
                                    // Focus the next notification item after deletion
                                    const items = getNotificationItems();
                                    const nextIndex = Math.min(index, items.length - 1);
                                    if (items[nextIndex]) {
                                        items[nextIndex].focus();
                                    }
                                }}
                                summary={summary}
                                status={status}
                                timestamp={timestamp}
                                zebra={index % 2 !== 0}
                            />
                        );
                    }
                )}
            </NotificationItems>
        );
    }

    function renderViewMore() {
        // Convert total to number for comparison (handles string "5" or number 5)
        const totalCount = parseInt(total, 10) || 0;
        const hasNotifications = totalCount > 0;
        return (
            <>
                {hasNotifications ? (
                    <ViewMoreStyled
                        gutter={isNavigationV2Enabled ? undefined : false}
                        onClick={onViewMore}
                        data-notification-item={isNavigationV2Enabled ? 'true' : undefined}
                        tabIndex={isNavigationV2Enabled ? 0 : undefined}
                    >
                        <Typography
                            fontSize="0.813rem"
                            lineHeight="1"
                            gutterBottom="0"
                            gutterTop="0"
                            type="link"
                            style={{
                                color: DEFAULT_COLORS.BLACK,
                                letterSpacing: '0.65px',
                                textDecoration: 'none',
                            }}
                        >{`VIEW ALL ${total} NOTIFICATIONS`}</Typography>
                    </ViewMoreStyled>
                ) : (
                    <ViewMoreStyled disableClick gutter={false} />
                )}
            </>
        );
    }

    const renderMenuContent = () => {
        return (
            <ContentStyled
                ref={contentRef}
                onMouseEnter={isNavigationV2Enabled ? cancelClose : undefined}
                onMouseLeave={isNavigationV2Enabled ? handleHideMenu : undefined}
                onBlur={isNavigationV2Enabled ? handleContentBlur : undefined}
            >
                {renderNotifications()}
                {!isEmpty(notifications) && (
                    <DeleteAllStyled
                        onClick={onClickDeleteAll}
                        // V2 keyboard navigation attributes
                        data-notification-item={isNavigationV2Enabled ? 'true' : undefined}
                        tabIndex={isNavigationV2Enabled ? 0 : undefined}
                    >
                        <ThrashIcon />
                        <Typography
                            color={DEFAULT_COLORS.BLACK}
                            fontSize="0.813rem"
                            gutterLeft="0.729rem"
                            gutterTop="0"
                            gutterBottom="0"
                            lineHeight="1"
                            style={{
                                letterSpacing: '0.65px',
                            }}
                        >
                            {MESSAGES.NOTIFICATIONS.DELETE_ALL}
                        </Typography>
                    </DeleteAllStyled>
                )}
                {renderViewMore()}
            </ContentStyled>
        );
    };

    const renderButtonComponent = () => {
        const pathName = get(history, 'location.pathname', '');
        const isNotificationList = pathName === '/notifications/list';
        const alertIconFillColor = isNavigationV2Enabled
            ? DEFAULT_COLORS.BLACK
            : getThemeProps('NavItem.styles.color', DEFAULT_COLORS.BLACK, {
                  theme,
              });
        const IconWrapperComponent = isNavigationV2Enabled ? IconWrapperV2 : IconWrapper;
        const NotificationsWrapper = isNavigationV2Enabled
            ? NotificationsWrapperV2
            : NotificationsWrapperV1;
        const NotificationsStyled = isNavigationV2Enabled
            ? NotificationsStyledV2
            : NotificationsStyledV1;

        const iconProps = isNavigationV2Enabled ? { width: '20', height: '20' } : {};

        return (
            <NotificationsWrapper>
                <NotificationsStyled>
                    <IconWrapperComponent>
                        {totalUnseen > 0 && !isNotificationList ? (
                            <>
                                <AlertIconCounter fill={alertIconFillColor} {...iconProps} />
                                <NotificationsCounter
                                    value={totalUnseen}
                                    isNavigationV2Enabled={isNavigationV2Enabled}
                                />
                            </>
                        ) : (
                            <AlertIcon fill={alertIconFillColor} {...iconProps} />
                        )}
                    </IconWrapperComponent>
                </NotificationsStyled>
            </NotificationsWrapper>
        );
    };

    const handleMouseEnterWithTooltip = useCallback(() => {
        handleShowMenu();
        if (isNavigationV2Enabled && onTooltipHover) {
            onTooltipHover();
        }
    }, [handleShowMenu, isNavigationV2Enabled, onTooltipHover]);

    const handleMouseLeaveWithTooltip = useCallback(() => {
        handleHideMenu();
        if (isNavigationV2Enabled && onTooltipLeave) {
            onTooltipLeave();
        }
    }, [handleHideMenu, isNavigationV2Enabled, onTooltipLeave]);

    const handleFocusWithTooltip = useCallback(() => {
        if (!isNavigationV2Enabled) {
            handleShowMenu();
        } else if (onTooltipHover) {
            onTooltipHover();
        }
    }, [handleShowMenu, isNavigationV2Enabled, onTooltipHover]);

    const handleBlurWithTooltip = useCallback(() => {
        if (!isNavigationV2Enabled) {
            handleHideMenu();
        } else if (onTooltipLeave) {
            onTooltipLeave();
        }
    }, [handleHideMenu, isNavigationV2Enabled, onTooltipLeave]);

    const menuWrapperContent = (
        <MenuWrapper
            className={
                Acl.check(APP_PERMISSIONS.userAssistanceClientAdmin)
                    ? 'pendo-nav-notifications'
                    : ''
            }
            onMouseLeave={handleMouseLeaveWithTooltip}
            onBlur={handleBlurWithTooltip}
            ref={node => {
                // Assign to both refs
                menuWrapperRef.current = node;
                if (iconRef) {
                    if (typeof iconRef === 'function') {
                        iconRef(node);
                    } else {
                        iconRef.current = node;
                    }
                }
            }}
            onClick={onClickNotificationIcon}
            onFocus={handleFocusWithTooltip}
            onMouseEnter={handleMouseEnterWithTooltip}
            tabIndex={isNavigationV2Enabled ? 0 : undefined}
            onKeyDown={isNavigationV2Enabled ? handleKeyDown : undefined}
            role={isNavigationV2Enabled ? 'button' : undefined}
            aria-label={isNavigationV2Enabled ? 'Notifications' : undefined}
            aria-haspopup={isNavigationV2Enabled ? 'menu' : undefined}
            aria-expanded={isNavigationV2Enabled ? isMenuOpen : undefined}
        >
            <Menu
                ButtonComponent={renderButtonComponent()}
                ContentComponent={PopoverStyles}
                ContentProps={{
                    borderRadius: isNavigationV2Enabled ? '4px' : '0',
                }}
                IconComponent={null}
                arrowSize={isNavigationV2Enabled ? 0 : 7}
                color="transparent"
                distanceFromContainer={0}
                placement="bottom-end"
                ref={menuRef}
                open={isMenuOpen}
                timeout={DURATION.none}
                borderRadius={4}
            >
                {renderMenuContent()}
            </Menu>
        </MenuWrapper>
    );

    if (isNavigationV2Enabled && tooltipText) {
        return (
            <Tooltip
                id="tooltip-notifications"
                content={tooltipText}
                padding="4px 16px"
                margin={0}
                arrowSize={5}
                placement="bottom"
                borderRadius="2px"
                open={showTooltip}
                disableHoverListener
                disableFocusListener
            >
                {menuWrapperContent}
            </Tooltip>
        );
    }

    return menuWrapperContent;
}

NotificationsMenu.propTypes = {
    errorMessage: string,
    hasError: bool,
    iconRef: oneOfType([func, object]),
    menuRef: oneOfType([func, object]),
    noNotificationsMessage: string,
    notifications: arrayOf(object),
    onClickNotification: func,
    onDeleteNotification: func,
    onClickNotificationIcon: func.isRequired,
    hideNotificationMenu: func.isRequired,
    showNotificationMenu: func.isRequired,
    onClickDeleteAll: func,
    onViewMore: func,
    open: bool,
    totalUnseen: string,
    total: string,
    history: object.isRequired,
    theme: object.isRequired,
    onMenuOpenChange: func,
    tooltipText: string,
    showTooltip: bool,
    onTooltipHover: func,
    onTooltipLeave: func,
};

NotificationsMenu.defaultProps = {
    errorMessage: null,
    hasError: false,
    iconRef: null,
    menuRef: null,
    noNotificationsMessage: 'No new Notifications',
    notifications: [],
    onClickNotification: null,
    onDeleteNotification: null,
    onClickDeleteAll: null,
    onViewMore: null,
    open: false,
    totalUnseen: null,
    total: null,
    onMenuOpenChange: null,
    tooltipText: null,
    showTooltip: false,
    onTooltipHover: null,
    onTooltipLeave: null,
};

export { NotificationsMenu as NotificationsMenuUnwrapped };
export default withTheme(NotificationsMenu);
